
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';

const FranchiseCompanies = ({ user, userData }) => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [franchiseId, setFranchiseId] = useState(null);
    const [filters, setFilters] = useState({
        industry: 'all',
        search: ''
    });
    const [stats, setStats] = useState({
        totalCompanies: 0,
        totalJobs: 0,
        totalApplications: 0
    });

    useEffect(() => {
        fetchFranchiseId();
    }, [user, userData]);

    useEffect(() => {
        if (franchiseId) {
            fetchCompanies();
        }
    }, [franchiseId]);

    useEffect(() => {
        filterCompanies();
    }, [companies, filters]);

    const fetchFranchiseId = async () => {
        try {
            // First try to get franchiseId from userData prop
            if (userData?.franchiseId) {
                console.log('Using franchiseId from userData:', userData.franchiseId);
                setFranchiseId(userData.franchiseId);
                return;
            }

            // Fallback to user.uid if no franchiseId
            if (user?.uid) {
                console.log('Using user.uid as franchiseId:', user.uid);
                setFranchiseId(user.uid);
            }
        } catch (error) {
            console.error('Error fetching franchiseId:', error);
        }
    };

    const fetchCompanies = async () => {
        setLoading(true);
        try {
            console.log('Fetching companies for franchiseId:', franchiseId);

            // Get all companies that belong to this franchise
            const companiesQuery = query(
                collection(db, 'companies'),
                where('franchiseId', '==', franchiseId)
            );
            const companiesSnapshot = await getDocs(companiesQuery);
            console.log('Found companies:', companiesSnapshot.size);

            // Get all jobs
            const jobsSnapshot = await getDocs(collection(db, 'jobs'));
            console.log('Total jobs:', jobsSnapshot.size);

            // Get all applications
            const applicationsSnapshot = await getDocs(collection(db, 'applications'));
            console.log('Total applications:', applicationsSnapshot.size);

            const companiesData = companiesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            console.log('Companies data:', companiesData);

            // Count jobs per company
            const jobCounts = {};
            const applicationCounts = {};

            jobsSnapshot.docs.forEach(jobDoc => {
                const job = jobDoc.data();
                const companyKey = job.companyId || job.userId;
                if (companyKey) {
                    jobCounts[companyKey] = (jobCounts[companyKey] || 0) + 1;

                    // Count applications for this job
                    const jobApplications = applicationsSnapshot.docs.filter(appDoc => {
                        const app = appDoc.data();
                        return app.jobId === jobDoc.id;
                    });

                    applicationCounts[companyKey] = (applicationCounts[companyKey] || 0) + jobApplications.length;
                }
            });

            console.log('Job counts:', jobCounts);
            console.log('Application counts:', applicationCounts);

            // Add job and application counts to companies
            const companiesWithCounts = companiesData.map(company => ({
                ...company,
                jobCount: jobCounts[company.id] || jobCounts[company.userId] || 0,
                applicationCount: applicationCounts[company.id] || applicationCounts[company.userId] || 0
            }));

            console.log('Companies with counts:', companiesWithCounts);

            setCompanies(companiesWithCounts);

            // Calculate stats
            const totalJobs = companiesWithCounts.reduce((sum, company) => sum + (company.jobCount || 0), 0);
            const totalApplications = companiesWithCounts.reduce((sum, company) => sum + (company.applicationCount || 0), 0);

            setStats({
                totalCompanies: companiesWithCounts.length,
                totalJobs,
                totalApplications
            });

            console.log('Stats calculated:', {
                totalCompanies: companiesWithCounts.length,
                totalJobs,
                totalApplications
            });

        } catch (error) {
            console.error('Error fetching companies:', error);
            alert('Error loading companies. Please check console for details.');
        } finally {
            setLoading(false);
        }
    };

    const filterCompanies = () => {
        let filtered = [...companies];

        if (filters.industry !== 'all') {
            filtered = filtered.filter(company => company.industry === filters.industry);
        }

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(company => {
                const locationText = `${company.city || ''} ${company.state || ''}`.toLowerCase();
                return company.name?.toLowerCase().includes(searchTerm) ||
                    company.companyName?.toLowerCase().includes(searchTerm) ||
                    company.industry?.toLowerCase().includes(searchTerm) ||
                    locationText.includes(searchTerm);
            });
        }

        setFilteredCompanies(filtered);
    };

    const getUniqueIndustries = () => {
        const industries = companies.map(company => company.industry).filter(Boolean);
        return [...new Set(industries)];
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        const dateObj = date.toDate ? date.toDate() : new Date(date);
        return dateObj.toLocaleDateString();
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">My Franchise Companies</h1>
                <p className="text-gray-600">Monitor companies and their job postings</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-200">
                    <div className="flex items-center">
                        <div className="p-3 bg-teal-500 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Companies</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.totalCompanies}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-200">
                    <div className="flex items-center">
                        <div className="p-3 bg-emerald-500 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Jobs Posted</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.totalJobs}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                    <div className="flex items-center">
                        <div className="p-3 bg-blue-500 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Applications</p>
                            <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search Companies</label>
                        <input
                            type="text"
                            placeholder="Search by name, industry, location..."
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                        <select
                            value={filters.industry}
                            onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        >
                            <option value="all">All Industries</option>
                            {getUniqueIndustries().map(industry => (
                                <option key={industry} value={industry}>{industry}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={() => setFilters({ industry: 'all', search: '' })}
                            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Companies List */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">
                        My Companies ({filteredCompanies.length})
                    </h2>
                </div>

                {filteredCompanies.length > 0 ? (
                    <div className="divide-y divide-gray-200">
                        {filteredCompanies.map((company) => (
                            <div
                                key={company.id}
                                className="p-6 hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-lg">
                                                {(company.companyName || company.name)?.charAt(0)?.toUpperCase() || 'C'}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {company.companyName || company.name}
                                                </h3>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                            <div className="flex items-center text-gray-600">
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                <span className="text-sm">{company.industry || 'Not specified'}</span>
                                            </div>

                                            <div className="flex items-center text-gray-600">
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                <span className="text-sm">
                                                    {company.city || company.state
                                                        ? `${company.city || ''}, ${company.state || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                                                        : 'Not specified'
                                                    }
                                                </span>
                                            </div>

                                            <div className="flex items-center text-gray-600">
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                <span className="text-sm font-semibold text-emerald-600">{company.jobCount} Jobs Posted</span>
                                            </div>

                                            <div className="flex items-center text-gray-600">
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <span className="text-sm font-semibold text-blue-600">{company.applicationCount} Applications</span>
                                            </div>
                                        </div>

                                        {company.description && (
                                            <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                                                {company.description.length > 150
                                                    ? company.description.substring(0, 150) + '...'
                                                    : company.description
                                                }
                                            </p>
                                        )}

                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-500">Registered: {formatDate(company.createdAt)}</span>
                                            <div className="flex items-center gap-2">
                                                {company.email && (
                                                    <span className="text-gray-600">{company.email}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <div className="text-gray-400 text-6xl mb-4">🏢</div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No companies found</h3>
                        <p className="text-gray-500 mb-4">No companies match your current filters.</p>
                        <button
                            onClick={() => navigate('/franchise/add-employer')}
                            className="px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-lg font-medium shadow-lg transition-all duration-300"
                        >
                            Add Your First Employer
                        </button>
                    </div>
                )}
            </div>
        </div >
    );
};

export default FranchiseCompanies;
